import { EmptyState, Skeleton } from '@h2hspark/ui';
import { Sparkles } from 'lucide-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { useAttendeeRoster, useMyMeetList } from '../attendee-preview/api';
import {
  NO_MATCHES_THIS_TIME_DESCRIPTION,
  NO_MATCHES_THIS_TIME_TITLE,
  NO_MATCHES_YET_DESCRIPTION,
  NO_MATCHES_YET_TITLE,
} from '../attendee-preview/constants';
import { PER_EVENT_LOCKED } from '../event-registrations/constants';
import { PerEventShell } from '../event-registrations/per-event-page';

import { useMyMatchSet } from './api';
import { ATTENDEES_REGION_ID, matchesHeading, matchesSubhead } from './constants';
import { EveryoneAttending, MeetOnlyToggle } from './everyone-attending-panel';
import { MatchCard } from './match-card';
import type { MatchSet } from './types';

/**
 * **The Matches page** — `Web App.dc.html` §2.4, `showMatches`, lines 401–480
 * (`MEM-025` AC-1, `MEM-013` AC-4, `MEM-027` AC-3/AC-4, `MEM-053` AC-1, `MEM-056` AC-2).
 *
 * ## Top to bottom, as the prototype draws it
 *
 * The back link to Your Events and the event header card with its stepper, *Before · Matches*
 * active — both `PerEventShell`'s, because five pages open the same way. Then a heading row: the
 * heading, a sentence beneath it, and the **People I want to meet** toggle with its count at the
 * right. Then a **3-column** grid of match cards (§3.8). Then *Everyone attending* at 44px, with
 * its own sentence and its own search pill, over a **2-column** grid of attendee cards (§3.9) and
 * the private-attendees row spanning both.
 *
 * On a phone it is one column throughout and the toggle drops under the heading (`MEM-056` AC-2);
 * the attendee grid opens to two at `md` and the matches to three at `lg`, because a match card
 * carries a paragraph and a button column and does not read at a third of 768px.
 *
 * ## Everyone attending is shared with the roster page
 *
 * This screen carries the whole room, which is what the prototype merged (`Spec - Matches +
 * Follow Up.dc.html`). Since Brad's updated design of 2026-09-25 (TW 44189308),
 * `/app/events/:id/attendees` — "See all" under Who's coming — draws the same section under the
 * same header card, without the match grid; the section is `everyone-attending-panel.tsx` so the
 * two cannot drift. The search is safe on both for one reason: only a **registrant** can read the
 * roster (`MEM-013`, and the server's `registrants-only` closure), so it searches a list the member
 * is already entitled to see in full. Held to the four identifying fields rather than the
 * conversation starter; `attendeesMatching` carries the argument.
 *
 * ## Three states of the match set, and the line between them
 *
 * `matchSet: null` is every reason there is nothing to show — no set, an unreleased set, an
 * opted-out member, an event that is not there — collapsed by the API on purpose, because the
 * difference is the release schedule and that is not a member's to know. A **released** set with
 * nobody in it is a fifth state and the member's own (`MEM-027` AC-3), and it says so.
 *
 * In both cases *Everyone attending* is still drawn beneath: the room exists whether or not the
 * matching found anybody in it, and `NO_MATCHES_THIS_TIME_DESCRIPTION` ends by saying so.
 */
export function EventMatchesPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();

  return (
    <PerEventShell eventId={eventId} surface="matches" locked={PER_EVENT_LOCKED.matches}>
      {({ event }) => <MatchesBody eventId={eventId} hasEnded={event.hasEnded} />}
    </PerEventShell>
  );
}

function MatchesBody({
  eventId,
  hasEnded,
}: {
  readonly eventId: string;
  readonly hasEnded: boolean;
}): React.ReactElement {
  const matchQuery = useMyMatchSet(eventId, { enabled: true });
  const rosterQuery = useAttendeeRoster(eventId, { enabled: true });
  const meetQuery = useMyMeetList(eventId, { enabled: true });

  const [meetOnly, setMeetOnly] = React.useState(false);
  const [query, setQuery] = React.useState('');

  /*
    `MEM-025` AC-8 — switching the toggle on brings the list it filters into view (TW 44182895;
    Sumit, 2026-09-24). The toggle is up here and *Everyone attending* is below the match grid, or
    below the empty box when there are no matches, so the filter otherwise changes something off
    screen. The scroll runs after the render that applies the filter, so it lands on the filtered
    list. Switching off leaves the page where it is.
  */
  const scrollToRoom = React.useRef(false);
  React.useEffect(() => {
    if (!scrollToRoom.current) return;
    scrollToRoom.current = false;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document
      .getElementById(ATTENDEES_REGION_ID)
      ?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [meetOnly]);

  const set = matchQuery.data?.matchSet ?? null;
  const meetList = meetQuery.data?.meetList ?? null;
  const savedRefs = React.useMemo(
    () => new Set((meetList?.entries ?? []).map((entry) => entry.attendeeRef)),
    [meetList],
  );

  const subhead = matchesSubhead({ hasEnded, count: set?.suggestions.length ?? 0 });

  return (
    <div className="space-y-7">
      {/* §2.4, item 4's heading row: `flex; align-items:flex-end; space-between; gap:20px; wrap`. */}
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <h2 className="mb-1.5 text-2xl tracking-tight md:text-3xl">{matchesHeading(hasEnded)}</h2>
          {/* Null while a live event has nothing to list: the empty box below says it instead. */}
          {subhead === null ? null : (
            <p className="max-w-[560px] text-md text-muted-foreground md:text-lg">{subhead}</p>
          )}
        </div>
        <MeetOnlyToggle
          count={savedRefs.size}
          on={meetOnly}
          onToggle={() => {
            scrollToRoom.current = !meetOnly;
            setMeetOnly(!meetOnly);
          }}
        />
      </div>

      {matchQuery.isPending ? (
        /* Three placeholders in the grid, at every width — not two of them hidden below `lg`.
           A `hidden md:block` here would be a breakpoint that removes an element, which
           `responsive-behaviour.ts` requires to be declared with a sentence about what a member on
           the other side can no longer do; the honest sentence for a loading placeholder is
           *nothing*, so it does not belong on an element that disappears. They stack on a phone,
           which is what the cards they stand in for do. */
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-[260px] w-full rounded-xl" />
          <Skeleton className="h-[260px] w-full rounded-xl" />
          <Skeleton className="h-[260px] w-full rounded-xl" />
        </div>
      ) : set === null ? (
        <EmptyState
          icon={<Sparkles className="h-6 w-6" aria-hidden />}
          title={NO_MATCHES_YET_TITLE}
          description={NO_MATCHES_YET_DESCRIPTION}
        />
      ) : set.suggestions.length === 0 ? (
        // `MEM-027` AC-3/AC-4. `MatchSet.status` is narrowed to `released`, so holding a set at all
        // means matching ran — and it found nobody worth putting in front of this member.
        <EmptyState
          icon={<Sparkles className="h-6 w-6" aria-hidden />}
          title={NO_MATCHES_THIS_TIME_TITLE}
          description={NO_MATCHES_THIS_TIME_DESCRIPTION}
        />
      ) : (
        <MatchGrid set={set} />
      )}

      <EveryoneAttending
        eventId={eventId}
        roster={rosterQuery.data?.roster}
        pending={rosterQuery.isPending}
        savedRefs={savedRefs}
        savedTotal={savedRefs.size}
        meetOnly={meetOnly}
        query={query}
        onQuery={setQuery}
        onBrowseEveryone={() => {
          setMeetOnly(false);
          setQuery('');
        }}
      />
    </div>
  );
}

/** §2.4: `repeat(3, minmax(0,1fr)); gap:24px`, one column on a phone and two from `md`. */
function MatchGrid({ set }: { readonly set: MatchSet }): React.ReactElement {
  return (
    <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {set.suggestions.map((suggestion) => (
        <li key={suggestion.suggestionRef} className="min-w-0">
          {/* No chip here: `MEM-025` AC-3 puts it on the cross-event view only, and this screen is
              already headed by the event. */}
          <MatchCard suggestion={suggestion} set={set} showEventChip={false} />
        </li>
      ))}
    </ul>
  );
}
